import React, { useEffect, useMemo, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import PropTypes from "prop-types";
import theme from "@styles/theme";
import { Grid } from "@mzc-pdc/ui";
import { alpha } from "@mui/material";

const WidgetWrap = ({ id, onMove, fullWidth, children, blockDrag, isNotice }) => {
    const [{ isDragging, handlerId }, connectDrag] = useDrag({
        item: { id, type: `WIDGET` },
        collect: (monitor) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
    });
    const [, connectDrop] = useDrop({
        accept: `WIDGET`,
        hover(hoveredOverItem) {
            const hoverItemId = hoveredOverItem.id;
            if (hoverItemId !== id) onMove(hoverItemId, id);
        },
    });
    const ref = useRef(null);

    const customClassName = useMemo(() => {
        let className = `widget`;
        if (isNotice) className += ` widget-notice`;

        if (fullWidth) className += ` full-width`;
        else {
            if (isDragging) className += ` dragging`;
        }
        return className;
    }, [isNotice, fullWidth, isDragging]);

    const widgetWidth = useMemo(() => {
        if (isNotice) return "calc(50% - 12px) !important";
        else if (fullWidth) return "100% !important";
        else return "calc(33.3333% - 16px) !important";
    }, []);

    const widgetHeight = useMemo(() => {
        if (isNotice) return 224;
        return 480;
    }, []);

    const draggingStyle = useMemo(() => {
        if (isDragging)
            return {
                border: `1px dashed ${theme.palette.greySecondary["A200"]} !important`,
                backgroundColor: `${theme.palette.background.default} !important`,
                "&::after": {
                    backgroundColor: `${alpha(theme.palette.greySecondary["A100"], 0.6)} !important`,
                },
            };
        else
            return {
                border: `1px solid ${theme.palette.greySecondary[100]} !important`,
                backgroundColor: `${theme.palette.background.default} !important`,
            };
    }, [isDragging]);

    useEffect(() => {
        if (blockDrag) return;
        connectDrag(ref);
        connectDrop(ref);
    }, [ref]);

    return (
        <Grid
            ref={ref}
            key={handlerId}
            data-handler-id={handlerId}
            item
            className={`${customClassName}`}
            sx={{
                margin: `0 !important`,
                borderRadius: "6px",
                boxShadow: `0px 2px 4px 0px rgba(0, 0, 0, 0.12)`,
                width: widgetWidth,
                height: widgetHeight,
                border: `1px solid ${theme.palette.greySecondary[100]}`,
                ...draggingStyle,
            }}
        >
            {children}
        </Grid>
    );
};

WidgetWrap.propTypes = {
    id: PropTypes.string,
    onMove: PropTypes.func,
    children: PropTypes.node,
};

export default WidgetWrap;
